import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getProjectsFromFirestore } from '../firestore';
import { auth } from '../firebase';
import type { SavedProject } from '../types';
import Hls from 'hls.js';

interface StreamProjectItem {
    id: string;
    projectName: string;
    updatedAt: number;
    videos: {
        label: string;
        url: string;
        hasStreamUrl: boolean;
        isExport: boolean;
    }[];
    needsStreamUpdate: boolean;
    originalProject: SavedProject;
}

const CLOUDFLARE_STREAM_HOST = 'customer-qhiiqpognkl0irb8.cloudflarestream.com';



const streamIdToUrl = (streamId?: string | null): string | null => {
    if (!streamId) return null;
    const id = streamId.trim();
    if (!id) return null;
    if (id.startsWith('http://') || id.startsWith('https://')) return id;
    const cleanId = id.startsWith('stream://') ? id.replace('stream://', '') : id;
    return `https://${CLOUDFLARE_STREAM_HOST}/${cleanId}/manifest/video.m3u8`;
};

const isStreamServerUrl = (url?: string | null): boolean => {
    if (!url) return false;
    const u = url.toLowerCase();
    return (
        u.startsWith('stream://') ||
        u.includes('cloudflarestream.com') ||
        u.includes('videodelivery.net') ||
        u.includes('watch.cloudflarestream.com') ||
        u.includes('/manifest/video.m3u8') ||
        u.includes('.m3u8') ||
        u.includes('.mpd') ||
        u.includes('/dash/')
    );
};

const pickVideoUrl = (item: { url?: string; urlMP4?: string; urlWebM?: string }): string | null => {
    return item.urlMP4 || item.urlWebM || item.url || null;
};

const projectToStreamItem = (project: SavedProject): StreamProjectItem | null => {
    const videos: StreamProjectItem['videos'] = [];
    let needsStreamUpdate = false;

    // 1. Process Exports
    (project.exports || []).forEach((exp, index) => {
        const url = pickVideoUrl(exp);
        if (url && !url.startsWith('blob:')) {
            videos.push({
                label: exp.name || `Export ${index + 1}`,
                url,
                hasStreamUrl: isStreamServerUrl(url),
                isExport: true
            });
        }
    });

    // 2. Process Clips (flattened)
    const allClips = [
        ...(project.clips || []),
        ...(project.montageVersions?.flatMap(m => m.clips || []) || [])
    ];

    allClips
        .filter(clip => clip.type === 'video')
        .forEach((clip, index) => {
            // Prefer streamUrl, fallback to raw url
            const candidateUrl = clip.streamUrl || clip.url;

            if (candidateUrl && !candidateUrl.startsWith('blob:')) {
                const isCloudflare = isStreamServerUrl(candidateUrl);
                if (!isCloudflare) {
                    needsStreamUpdate = true;
                }

                videos.push({
                    label: clip.name || `Clip ${index + 1}`,
                    url: candidateUrl,
                    hasStreamUrl: isCloudflare,
                    isExport: false
                });
            }
        });

    if (videos.length === 0) {
        return null; // Don't show projects without remote videos
    }

    // Remove duplicates based on URL
    const uniqueVideos: typeof videos = [];
    const seenUrls = new Set();
    for (const v of videos) {
        if (!seenUrls.has(v.url)) {
            seenUrls.add(v.url);
            uniqueVideos.push(v);
        }
    }

    return {
        id: project.id,
        projectName: project.name,
        updatedAt: project.updatedAt || Date.now(),
        videos: uniqueVideos,
        needsStreamUpdate,
        originalProject: project
    };
};

const HlsVideo: React.FC<{ src: string }> = ({ src }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let hls: Hls | null = null;
        if (src.includes('.m3u8') && Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(src);
            hls.attachMedia(video);
        } else {
            video.src = src;
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
        };
    }, [src]);

    return (
        <video
            ref={videoRef}
            controls
            autoPlay
            playsInline
            style={{ width: '100%', borderRadius: 10, background: '#000' }}
        />
    );
};

export const StreamPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [projects, setProjects] = useState<StreamProjectItem[]>([]);
    const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
    const [updatingParams, setUpdatingParams] = useState<{ id: string, progress: string } | null>(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    setLoading(true);
                    setError(null);
                    const dbProjects = await getProjectsFromFirestore();
                    console.log(`[StreamPage] Loaded ${dbProjects.length} projects from Firestore.`);

                    const streamProjects = dbProjects.map(projectToStreamItem).filter((p): p is StreamProjectItem => p !== null);
                    // Sort by updatedAt descending
                    streamProjects.sort((a, b) => b.updatedAt - a.updatedAt);

                    console.log(`[StreamPage] Projects with videos: ${streamProjects.length}`);
                    setProjects(streamProjects);

                    if (streamProjects[0]?.videos[0]) {
                        setActiveVideoUrl(streamProjects[0].videos[0].url);
                    }
                } catch (err: any) {
                    setError(err?.message || 'Impossible de charger les projets.');
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
                setProjects([]);
            }
        });

        return () => unsubscribe();
    }, []);

    const handleUpdateProjectStreams = async (projectItem: StreamProjectItem) => {
        try {
            setUpdatingParams({ id: projectItem.id, progress: 'Démarrage...' });
            const project = projectItem.originalProject;
            const exportClient = (await import('../server-export-client')).getServerExportClient(auth.currentUser?.uid || 'anonymous');

            let updatedCount = 0;

            // Fonction récursive de mise à jour (similaire à App.tsx)
            const processClipRecursive = async (inputClip: any): Promise<{ clip: any, modified: boolean }> => {
                let clip = { ...inputClip };
                let modified = false;

                const hasValidStreamUrl = clip.streamUrl && (clip.streamUrl.includes('cloudflarestream.com') || clip.streamUrl.includes('videodelivery.net'));

                // Fix Stream URL for videos
                if (clip.type === 'video' && clip.url && !clip.url.startsWith('blob:') && !hasValidStreamUrl) {
                    setUpdatingParams({ id: projectItem.id, progress: `Upload de ${clip.name || 'vidéo'}...` });
                    try {
                        const streamResult = await exportClient.uploadToStream(clip.url, clip.id);
                        if (streamResult.success && streamResult.streamUrl) {
                            clip.streamUrl = streamResult.streamUrl;
                            if (clip.chunks) delete clip.chunks;
                            modified = true;
                            updatedCount++;
                        }
                    } catch (e) {
                        console.warn(`[StreamPage] Failed to migrate streamUrl for clip ${clip.id}`, e);
                    }
                } else if (clip.type === 'video' && hasValidStreamUrl && clip.chunks && clip.chunks.length > 0) {
                    delete clip.chunks;
                    modified = true;
                    updatedCount++;
                }

                if (clip.syntheData?.clips?.length) {
                    const processedNestedClips = [];
                    let nestedModified = false;
                    for (const nestedClip of clip.syntheData.clips) {
                        const nestedResult = await processClipRecursive(nestedClip);
                        processedNestedClips.push(nestedResult.clip);
                        if (nestedResult.modified) nestedModified = true;
                    }
                    if (nestedModified) {
                        clip.syntheData = { ...clip.syntheData, clips: processedNestedClips };
                        modified = true;
                    }
                }
                return { clip, modified };
            };

            let projectModified = false;
            const updatedClips = [];

            for (let i = 0; i < project.clips.length; i++) {
                const result = await processClipRecursive(project.clips[i]);
                updatedClips.push(result.clip);
                if (result.modified) projectModified = true;
            }

            if (projectModified) {
                setUpdatingParams({ id: projectItem.id, progress: 'Sauvegarde...' });
                const updatedProject = { ...project, clips: updatedClips, updatedAt: Date.now() };
                const { saveProjectToFirestore } = await import('../firestore');
                await saveProjectToFirestore(updatedProject);

                // Rafraichir la liste localement en conservant l'objet (ou un nouveau clone)
                const updatedStreamItem = projectToStreamItem(updatedProject);
                setProjects(prev => prev.map(p =>
                    p.id === project.id && updatedStreamItem ? updatedStreamItem : p
                ));
                alert(`Mise à jour réussie : ${updatedCount} vidéo(s) envoyée(s) sur Stream.`);
            } else {
                alert('Rien à mettre à jour (les vidéos ont déjà un streamUrl valide).');
            }
        } catch (e: any) {
            alert(`Erreur: ${e.message}`);
        } finally {
            setUpdatingParams(null);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#0f1115', color: '#e6e8eb', fontFamily: 'system-ui, sans-serif' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
                <h1 style={{ margin: 0, fontSize: 24 }}>Stream test</h1>
                <p style={{ margin: '8px 0 16px', color: '#9ca3af' }}>
                    Page de test: lecture simple des vidéos de tes projets.
                </p>

                {loading && <p>Chargement...</p>}
                {!loading && error && <p style={{ color: '#f87171' }}>{error}</p>}
                {!loading && !error && projects.length === 0 && (
                    <p>Aucune vidéo trouvée dans tes projets.</p>
                )}

                {!loading && !error && projects.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
                        {activeVideoUrl ? (
                            <div>
                                <HlsVideo key={activeVideoUrl} src={activeVideoUrl} />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', borderRadius: 10, minHeight: 400 }}>
                                Selectionne une vidéo
                            </div>
                        )}

                        <div style={{ maxHeight: '70vh', overflow: 'auto', border: '1px solid #22262d', borderRadius: 10 }}>
                            {projects.map(project => {
                                return (
                                    <div key={project.id} style={{ borderBottom: '1px solid #22262d' }}>
                                        <div style={{ padding: '12px', background: '#171a21', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontWeight: 600 }}>{project.projectName}</div>

                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <a
                                                    href={`/?project=${project.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#9ca3af', fontSize: 11, textDecoration: 'none', padding: '4px 8px', border: '1px solid #374151', borderRadius: 4 }}
                                                >
                                                    Ouvrir Éditeur ↗
                                                </a>
                                                {project.needsStreamUpdate ? (
                                                    <button
                                                        onClick={() => handleUpdateProjectStreams(project)}
                                                        disabled={updatingParams?.id === project.id}
                                                        style={{
                                                            background: '#3b82f6', color: 'white', border: 'none',
                                                            padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11,
                                                            opacity: updatingParams?.id === project.id ? 0.5 : 1
                                                        }}
                                                    >
                                                        {updatingParams?.id === project.id ? updatingParams.progress : 'Convertir en Stream'}
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: 11, color: '#10b981', background: '#ecfdf5', padding: '2px 6px', borderRadius: 4 }}>Stream 100% OK</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            {project.videos.map((v, i) => {
                                                const isActive = v.url === activeVideoUrl;
                                                return (
                                                    <div
                                                        key={i}
                                                        style={{
                                                            width: '100%', padding: '8px 12px 8px 24px',
                                                            borderBottom: i < project.videos.length - 1 ? '1px dotted #22262d' : 'none',
                                                            background: isActive ? '#1f2937' : 'transparent',
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                                        }}
                                                    >
                                                        <button
                                                            onClick={() => setActiveVideoUrl(v.url)}
                                                            style={{
                                                                flex: 1, textAlign: 'left', border: 'none', background: 'transparent',
                                                                cursor: 'pointer', color: isActive ? '#fff' : '#9ca3af',
                                                                display: 'flex', alignItems: 'center', gap: 8
                                                            }}
                                                        >
                                                            <span style={{ fontSize: 13 }}>{v.label} {v.isExport && '(Export)'}</span>
                                                            {!v.hasStreamUrl && <span style={{ fontSize: 10, color: '#f87171' }}>Non-stream</span>}
                                                        </button>
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                if (!confirm(`Supprimer la vidéo "${v.label}" ?\nCette action la supprimera du stockage (R2/Stream) mais la laissera "cassée" dans votre projet si vous ne la retirez pas du montage.`)) return;

                                                                const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';
                                                                try {
                                                                    // Extract key from URL
                                                                    let key = v.url;
                                                                    if (v.url.includes('pub-')) {
                                                                        const urlObj = new URL(v.url);
                                                                        key = decodeURIComponent(urlObj.pathname.substring(1));
                                                                    } else if (v.url.includes('storage.googleapis.com')) {
                                                                        // fallback, handled differently, but let's try
                                                                        const parts = v.url.split('/');
                                                                        key = parts.slice(4).join('/');
                                                                    } else {
                                                                        // Cannot parse R2 key easily from non-standard URLs
                                                                        const parts = v.url.split('/');
                                                                        if (parts.length > 3) key = parts.slice(3).join('/');
                                                                    }

                                                                    // Call delete endpoint
                                                                    const res = await fetch(`${EXPORT_SERVICE_URL}/media/delete`, {
                                                                        method: 'POST',
                                                                        headers: { 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify({ key })
                                                                    });

                                                                    if (!res.ok) throw new Error(await res.text());

                                                                    // Update UI: remove video from local state
                                                                    setProjects(prev => prev.map(p => {
                                                                        if (p.id === project.id) {
                                                                            return {
                                                                                ...p,
                                                                                videos: p.videos.filter(vid => vid.url !== v.url)
                                                                            };
                                                                        }
                                                                        return p;
                                                                    }).filter(p => p.videos.length > 0));

                                                                    if (activeVideoUrl === v.url) {
                                                                        setActiveVideoUrl(null);
                                                                    }
                                                                } catch (err) {
                                                                    alert('Erreur suppression: ' + err);
                                                                }
                                                            }}
                                                            style={{
                                                                background: 'transparent', border: 'none', cursor: 'pointer',
                                                                padding: '4px 8px', color: '#ef4444', borderRadius: 4,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                            }}
                                                            title="Supprimer la vidéo"
                                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
